import React, { useState, useEffect, createContext, useContext } from 'react';
import { Box, Alert, AlertIcon, AlertTitle, AlertDescription, Button, Spinner, VStack, Text, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { getActiveBatchForGame, createInitialBatch } from './BatchCreationHelper';

// Create a context for batch information
export const BatchContext = createContext({
  batch: null,
  loading: false,
  error: null,
  refreshBatch: () => {}
});

// Custom hook to use the batch context
export const useBatch = () => useContext(BatchContext);

/**
 * BatchProvider Component
 * 
 * Provides batch information to all child components
 */
export const BatchProvider = ({ children, gameId }) => {
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const loadBatch = async () => {
    setLoading(true);
    try {
      const activeBatch = getActiveBatchForGame(gameId);
      setBatch(activeBatch);
      setError(null);
    } catch (err) {
      console.error('Error loading batch:', err);
      setError('Failed to load batch information');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (gameId) {
      loadBatch();
    }
  }, [gameId]);
  
  return (
    <BatchContext.Provider value={{ batch, loading, error, refreshBatch: loadBatch }}>
      {children}
    </BatchContext.Provider>
  );
};

/**
 * BatchAvailabilityChecker Component
 * 
 * Checks if a batch is available for the game and shows appropriate UI
 */
export const BatchAvailabilityChecker = ({ gameId, gameConfig }) => {
  const { batch, loading, error, refreshBatch } = useBatch();
  const [creating, setCreating] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  
  const handleCreateBatch = async () => {
    setCreating(true);
    try {
      await createInitialBatch(gameConfig);
      toast({
        title: "Batch Created",
        description: "A new batch has been created for this game.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      refreshBatch();
    } catch (err) {
      console.error('Error creating batch:', err);
      toast({
        title: "Batch Creation Failed",
        description: "There was an error creating a new batch. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };
  
  if (loading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="md" />
        <Text mt={2}>Checking batch availability...</Text>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Alert status="error" borderRadius="md" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error}
            <Button onClick={refreshBatch} size="sm" ml={2} colorScheme="red">
              Retry
            </Button>
          </AlertDescription>
        </Box>
      </Alert>
    );
  }
  
  if (!batch) {
    return (
      <Alert status="warning" borderRadius="md" mb={4}>
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>No Active Batch</AlertTitle>
          <AlertDescription>
            There is no active batch for this game. Cards cannot be minted until a batch is created.
          </AlertDescription>
        </Box>
        <VStack spacing={2} align="flex-end">
          <Button 
            colorScheme="blue" 
            size="sm" 
            onClick={handleCreateBatch}
            isLoading={creating}
            loadingText="Creating..."
          >
            Create Batch
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/modular-profile/games/batch-dashboard')}
          >
            Batch Dashboard
          </Button>
        </VStack>
      </Alert>
    );
  }
  
  return (
    <Alert status="success" borderRadius="md" mb={4} variant="subtle">
      <AlertIcon />
      <Box flex="1">
        <AlertTitle>Batch Active</AlertTitle>
        <AlertDescription>
          Batch ID: {batch.id.substring(0, 8)}...
          <br />
          Cards Available: {batch.cardsRemaining || 'Unknown'}
        </AlertDescription>
      </Box>
      <Button 
        variant="outline" 
        size="sm" 
        onClick={() => navigate('/modular-profile/games/batch-dashboard')}
      >
        Manage Batches
      </Button>
    </Alert>
  );
};

/**
 * withBatchIntegration HOC
 * 
 * Higher-order component that wraps a game component with batch integration
 */
export const withBatchIntegration = (GameComponent) => {
  return (props) => {
    const { gameId, gameConfig, ...rest } = props;
    
    return (
      <BatchProvider gameId={gameId}>
        <BatchAvailabilityChecker gameId={gameId} gameConfig={gameConfig} />
        <GameComponent {...rest} gameId={gameId} gameConfig={gameConfig} />
      </BatchProvider>
    );
  };
};
